import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import rentsService from "../services/rentsService";
import styles from "../assets/rentGuardianOfficerEdit.module.css";
import Loader from "../components/Loader";

const RentGuardianOfficerEdit = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    guardian_officer_id: "",
  });
  const [rentData, setRentData] = useState(null);
  const [officers, setOfficers] = useState([]);

  useEffect(() => {
    loadRentData();
  }, [rentId]);

  const loadRentData = async () => {
    try {
      setLoading(true);
      const data = await rentsService.getGuardianOfficerEdit(rentId);
      console.log("🔍 [FRONTEND DEBUG] API Response:", data);
      console.log("🔍 [FRONTEND DEBUG] Officers:", data.officers);
      console.log("🔍 [FRONTEND DEBUG] Officers type:", typeof data.officers);
      console.log("🔍 [FRONTEND DEBUG] Officers is array:", Array.isArray(data.officers));
      
      setRentData(data.rent);
      // Ensure officers is always an array
      const officersArray = Array.isArray(data.officers) ? data.officers : [];
      console.log("🔍 [FRONTEND DEBUG] Setting officers:", officersArray.length, "items");
      setOfficers(officersArray);
      setFormData({
        guardian_officer_id: data.rent?.guardian_officer_id || "",
      });
    } catch (err) {
      console.error("❌ [FRONTEND ERROR] Error loading rent data:", err);
      console.error("❌ [FRONTEND ERROR] Error details:", err.response?.data || err.message);
      setRentData(null);
      setOfficers([]);
      setFormData({ guardian_officer_id: "" });
      toast.error("Failed to load guardian officer data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.guardian_officer_id) {
      toast.error("Please select a guardian officer");
      return;
    }

    try {
      setSaving(true);
      await rentsService.updateGuardianOfficer(rentId, formData);
      toast.success("Guardian officer updated successfully");
      navigate("/rents");
    } catch (err) {
      console.error("Error updating guardian officer:", err);
      toast.error("Failed to update guardian officer");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/rents");
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles["rent-guardian-officer-edit"]}>
        <div className={styles["error-container"]}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles["rent-guardian-officer-edit"]}>
      {/* Breadcrumb */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/dashboard">Dashboard</a>
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/rents/after-date-end">/ Placed guardians after date end</a>
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Manage Officer</span>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles["main-content"]}>
        <div className={styles["panel"]}>
          <div className={styles["panel-heading"]}>
            <h3 className={styles["panel-title"]}>Manage Guardian Officer</h3>
          </div>
          <div className={styles["panel-body"]}>
            {/* Guardian Overview */}
            <div className={styles["guardian-overview"]}>
              <h4>Guardian Overview</h4>
              <div className={styles["overview-grid"]}>
                <div className={styles["overview-item"]}>
                  <label>UGD:</label>
                  <span>
                    {rentData?.unique_guardian_number || "Loading..."}
                  </span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Guardian:</label>
                  <span>{rentData?.guardianName || "Loading..."}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Room:</label>
                  <span>{rentData?.roomName || "Loading..."}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Property:</label>
                  <span>{rentData?.propertyName || "Loading..."}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Current Officer:</label>
                  <span>{rentData?.currentOfficerName || "Loading..."}</span>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <div className={styles["edit-form"]}>
              <h4>Change Guardian Officer</h4>
              <form onSubmit={handleSubmit}>
                <div className={styles["form-group"]}>
                  <label htmlFor="guardian_officer_id">
                    Guardian Officer *
                  </label>
                  <select
                    id="guardian_officer_id"
                    name="guardian_officer_id"
                    className={styles["form-control"]}
                    value={formData.guardian_officer_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Guardian Officer</option>
                    {officers.map((officer) => (
                      <option key={officer.id} value={officer.id}>
                        {officer.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles["form-actions"]}>
                  <button
                    type="submit"
                    className={styles["btn-primary"]}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentGuardianOfficerEdit;
